import { useState } from 'react';
import {
  AiTextGeneratorFormValues,
  groqFields,
} from '../AiConfigs/constants';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PrettoSlider } from '../../assets/styles/styles';
import { useAuthStore } from '../../store/storeAuth';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import { Info } from '@phosphor-icons/react';

type Props = {
  watch: AiTextGeneratorFormValues;
  onChange: (e: any) => void;
  systems?: any[];
};

const modelInputBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '4px',
};

const GroqAi = ({ onChange, watch, systems }: Props) => {
  const [isAutoSetting, setIsAutoSetting] = useState(watch.autoToken);
  const { user } = useAuthStore(state => state);

  const groqSystem = systems?.filter((item: any) => item.system === 'groq')[0];
  const textModels = (groqSystem?.models || []).filter(
    (m: any) => m.type === 'Text' && !m.isDeprecated,
  );

  return (
    <Box>
      <Box mt={'4px'}>
        <Typography
          fontSize={'14px'}
          fontWeight={500}
          sx={{
            mb: '4px',
            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
          }}
          variant="body1"
        >
          Model
        </Typography>
        <FormControl sx={{ width: '100%' }}>
          <Select
            className="nodrag"
            name="groqModel"
            fullWidth
            size="small"
            value={watch.groqModel}
            sx={{
              height: 'unset',
              bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            }}
            onChange={onChange}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: '400px', maxWidth: '600px' },
              },
            }}
          >
            {textModels.map((opt: any, index: number) => (
              <MenuItem
                className="nodrag"
                key={opt.model + index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                  padding: '10px 14px',
                  whiteSpace: 'normal',
                }}
                value={opt.model}
                disabled={opt.enabled === false || opt.disabledByCompany}
              >
                <Box display="flex" alignItems="center" gap="6px">
                  <Typography
                    fontSize="13px"
                    fontWeight={500}
                    textTransform="capitalize"
                    color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                  >
                    {opt.displayName || opt.model}
                  </Typography>
                  {opt.disabledByCompany && (
                    <Typography fontSize="11px" color="red">
                      (disabled by admin)
                    </Typography>
                  )}
                </Box>
                {opt?.tags?.length > 0 && (
                  <Box display="flex" gap="4px" alignItems="center" flexWrap="wrap" mt="4px">
                    {opt.tags.map((tag: any, tagIdx: number) => {
                      const tagLabel = typeof tag === 'string' ? tag : tag.label;
                      const tagTooltip = typeof tag === 'string' ? '' : tag.tooltip;
                      return (
                        <Tooltip key={tagLabel + tagIdx} title={tagTooltip} arrow placement="top">
                          <Chip
                            size="small"
                            sx={{
                              borderRadius: '4px',
                              fontSize: '11px',
                              height: '20px',
                              border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
                              color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                            }}
                            label={tagLabel}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
                {opt.description && (
                  <Typography
                    fontSize="12px"
                    sx={{
                      color: '#667085',
                      mt: '4px',
                      whiteSpace: 'normal',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {opt.description}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {groqFields.map(opt => {
        const optName = opt.name;
        if (opt.name === 'groqMaxTokens') {
          return (
            <Box
              sx={{
                ...modelInputBoxStyle,
                gap: '30px',
                alignItems: 'center',
                mt: '4px',
              }}
              key={optName}
            >
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={e => {
                        onChange({
                          target: {
                            name: 'autoToken',
                            value: !watch.autoToken,
                          },
                        });

                        setIsAutoSetting(!watch.autoToken);
                      }}
                      name="autoToken"
                      checked={watch.autoToken}
                      sx={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    />
                  }
                  label="Auto setting"
                />
              </Box>
              <>
                <FormLabel htmlFor="tokens">{opt.label}</FormLabel>
                <TextField
                  type={opt.type}
                  style={{ marginLeft: '8px', width: '100px' }}
                  onChange={onChange}
                  value={isAutoSetting ? 0 : watch.groqMaxTokens}
                  disabled={isAutoSetting}
                  className="nodrag"
                  name={opt.name}
                  InputProps={{
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </>
            </Box>
          );
        }
        return (
          <Box key={opt.name}>
            <FormLabel
              htmlFor={opt.name}
              sx={{
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
              }}
            >
              {opt.label}
            </FormLabel>
            <Box display={'flex'} alignItems={'center'} gap={'8px'}>
              <FormLabel htmlFor={opt.name}>
                {watch?.[optName as keyof AiTextGeneratorFormValues]}
              </FormLabel>

              <PrettoSlider
                step={Number(opt.step)}
                min={Number(opt.min)}
                max={Number(opt.max)}
                name={opt.name}
                value={Number(
                  watch?.[optName as keyof AiTextGeneratorFormValues],
                )}
                onChange={onChange}
                className="nodrag"
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <FormLabel
          sx={{
            mb: '4px',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
          }}
          htmlFor="groqStop"
        >
          Groq Stop
        </FormLabel>
        <IconButtonTooltip
          title={`  A stop sequence is a predefined or user-specified text string that
 signals an AI to stop generating content, ensuring its responses
 remain focused and concise. Examples include punctuation marks and
 markers like "[end]"`}
        >
          <Info size={20} />
        </IconButtonTooltip>
      </Box>
      <TextField
        type={'text'}
        fullWidth
        onChange={onChange}
        value={watch.groqStop}
        className="nodrag"
        name={'groqStop'}
        InputProps={{
          style: {
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
          },
        }}
        sx={{
          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
          bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
          border: `2px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
        }}
      />
    </Box>
  );
};

export default GroqAi;
